/**
 * Base64 detection and decoding utilities.
 *
 * Used by event detail views to surface human-readable content from
 * base64-encoded webhook payloads (e.g. Gmail/Pub-Sub `message.data`,
 * raw webhook bodies). The detection is conservative — false positives
 * are worse than false negatives because we'd be lying to the operator
 * about what the data actually is.
 */

// Standard base64 alphabet plus padding. We deliberately do not allow
// URL-safe base64 here (`-` / `_`) because JWT segments (which appear
// frequently in headers) use that variant and we already filter JWTs
// out structurally — they contain dots.
const BASE64_REGEX = /^[A-Za-z0-9+/]+={0,2}$/

/** Minimum length before we'll even consider a string as potentially base64. */
const MIN_LENGTH = 16

/**
 * Returns true if the string is composed entirely of hexadecimal characters.
 *
 * Hex identifiers — Cloud Logging `insert_id`, trace/span IDs, SHA digests —
 * are a strict subset of the base64 alphabet, so they sail past the length and
 * charset checks below and then "decode" to meaningless bytes. A genuine
 * base64 encoding of real text effectively never comes out as pure hex at
 * length >= 16 (it would need every byte to land in `[0-9a-f]`), so rejecting
 * hex-only strings removes the common false positive without hiding real data.
 */
function isHexString(value: string): boolean {
  return /^[0-9a-fA-F]+$/.test(value)
}

/**
 * Heuristic check: does this string look like it might be base64-encoded?
 * This is intentionally strict — it should reject most random IDs, hashes,
 * and JWT segments to avoid false positives.
 */
export function looksLikeBase64(value: string): boolean {
  if (value.length < MIN_LENGTH) return false
  // Standard base64 output length is always a multiple of 4 (with padding).
  if (value.length % 4 !== 0) return false
  if (!BASE64_REGEX.test(value)) return false
  // Hex-only IDs/digests (e.g. `insert_id`, trace IDs) structurally resemble
  // base64 but decode to garbage — never treat them as base64.
  if (isHexString(value)) return false
  return true
}

/**
 * Attempt to decode a base64 string to UTF-8 text. Returns `null` if the
 * input is not valid base64 or contains binary data that can't be decoded
 * as UTF-8.
 */
export function decodeBase64ToText(encoded: string): string | null {
  try {
    const binary = atob(encoded)
    // Convert the binary string to bytes, then decode as UTF-8. We use
    // `fatal: false` so partial / malformed UTF-8 doesn't throw, but we
    // check printability afterwards to filter out binary garbage.
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }
    const decoder = new TextDecoder('utf-8', { fatal: false })
    return decoder.decode(bytes)
  } catch {
    return null
  }
}

/**
 * Returns true if the decoded string is overwhelmingly printable text,
 * which is our signal that the base64 actually represents something
 * meaningful (JSON, plain text, etc.) rather than binary data like an
 * image or compressed blob.
 */
function isMostlyPrintable(text: string): boolean {
  if (text.length === 0) return false
  let printable = 0
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i)
    // Printable ASCII (32-126), tab (9), LF (10), CR (13).
    // Anything >=128 we treat as printable Unicode (likely UTF-8 multibyte).
    if ((c >= 32 && c <= 126) || c === 9 || c === 10 || c === 13 || c >= 128) {
      printable++
    }
  }
  return printable / text.length > 0.9
}

/**
 * Try to parse text as JSON. Returns `undefined` if the text is not a
 * valid JSON object or array. We require an object/array root so we
 * don't render bare JSON primitives (`"foo"`, `42`) as nested trees.
 */
export function tryParseJson(text: string): unknown {
  const trimmed = text.trim()
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return undefined
  try {
    return JSON.parse(trimmed)
  } catch {
    return undefined
  }
}

export interface Base64Analysis {
  /** True only if the value looks like base64 AND decodes to printable text. */
  isBase64: boolean
  /** The decoded UTF-8 text, when `isBase64` is true. */
  decoded?: string
  /** The decoded text parsed as JSON, when it is a valid JSON object/array. */
  json?: unknown
}

/**
 * Analyse a value to determine whether it is base64-encoded and, if so,
 * what the decoded form is. Returns `{ isBase64: false }` for any value
 * that isn't a string, doesn't pass the structural checks, or decodes
 * to binary/garbage.
 */
export function analyzeBase64(value: unknown): Base64Analysis {
  if (typeof value !== 'string') return { isBase64: false }
  if (!looksLikeBase64(value)) return { isBase64: false }

  const decoded = decodeBase64ToText(value)
  if (decoded == null) return { isBase64: false }
  if (!isMostlyPrintable(decoded)) return { isBase64: false }

  const json = tryParseJson(decoded)
  return { isBase64: true, decoded, json }
}
